import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OkButton } from 'src/components/Buttons/OkButton';
import { Content, useFormattedContent } from 'src/contexts/ContentContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { PosTextField } from 'src/core/POS/PosTextField';
import { vars } from 'src/core/themes';
import { typography } from 'src/core/themes/shared.css';
import { HighlightText, Stack } from 'src/core/ui';
import { useGetSalesInSalesGroup } from 'src/hooks/api/useRetailSaleDetails';
import {
  ModalBody,
  ModalBodyDataContainer,
  ModalFooter,
} from 'src/modals/Modal/Modal.styled';
import { ModalHeader } from 'src/modals/Modal/ModalHeader';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { formatCurrency } from 'src/utils/numberFormatter';
import {
  MarketplacePaymentLineType,
  MarketplacePaymentState,
  SaleLineItemCoverage,
} from 'src/WebApiController';

import * as styles from './PartialPaymentCoverageDialog.css';

type PartialPaymentCoverageDialogProps = {
  salesGroupId: string | null;
  partialAmount: number;
  currencyCode: string;
  onConfirm: (coverages: SaleLineItemCoverage[]) => void;
  onCancel: () => void;
};

const LINE_TYPE_LABELS: Partial<Record<MarketplacePaymentLineType, string>> = {
  [MarketplacePaymentLineType.Proceeds]: 'Proceeds',
  [MarketplacePaymentLineType.Credit]: 'Credit',
  [MarketplacePaymentLineType.FullPrice]: 'Full Price',
  [MarketplacePaymentLineType.SellerFee]: 'Seller Fee',
  [MarketplacePaymentLineType.Penalty]: 'Penalty',
  [MarketplacePaymentLineType.AlternativeTickets]: 'Alternative Tickets',
  [MarketplacePaymentLineType.OrderNotFulfilled]: 'Order Not Fulfilled',
  [MarketplacePaymentLineType.CancelledEvent]: 'Cancelled Event',
  [MarketplacePaymentLineType.WitheldFunds]: 'Withheld Funds',
  [MarketplacePaymentLineType.LastMinuteSalesLateDelivery]: 'Late Delivery',
};

const autoFillAmounts = (
  lines: {
    id: number;
    type: MarketplacePaymentLineType;
    amt: { amt: number };
  }[],
  total: number
): Record<number, number> => {
  const sorted = [...lines].toSorted((a, b) => {
    const aP = a.type === MarketplacePaymentLineType.Proceeds;
    const bP = b.type === MarketplacePaymentLineType.Proceeds;
    if (aP && !bP) return 1;
    if (!aP && bP) return -1;
    return 0;
  });
  const result: Record<number, number> = {};
  let remaining = total;
  for (const line of sorted) {
    if (remaining <= 0) {
      result[line.id] = 0;
      continue;
    }
    const alloc = Math.min(remaining, line.amt.amt);
    result[line.id] = Math.round(alloc * 100) / 100;
    remaining -= alloc;
  }
  return result;
};

export const PartialPaymentCoverageDialog = ({
  salesGroupId,
  partialAmount,
  currencyCode,
  onConfirm,
  onCancel,
}: PartialPaymentCoverageDialogProps) => {
  const { data, isLoading, isFetching } = useGetSalesInSalesGroup(salesGroupId);

  // The group shares one MarketplacePayment; the coverage spans every member sale's lines.
  const unpaidLines = useMemo(
    () =>
      (data ?? [])
        .flatMap((sale) => sale.mkpLines ?? [])
        .filter((sli) => sli.state === MarketplacePaymentState.Unpaid),
    [data]
  );

  const sortedLines = useMemo(
    () =>
      [...unpaidLines].toSorted((a, b) => {
        const aP = a.type === MarketplacePaymentLineType.Proceeds;
        const bP = b.type === MarketplacePaymentLineType.Proceeds;
        if (aP && !bP) return 1;
        if (!aP && bP) return -1;
        return 0;
      }),
    [unpaidLines]
  );

  const [amounts, setAmounts] = useState<Record<number, number>>({});
  const hasAutoFilled = useRef(false);

  useEffect(() => {
    if (isFetching) {
      hasAutoFilled.current = false; // Reset so auto-fill reruns when fresh data arrives
    }
  }, [isFetching]);

  useEffect(() => {
    if (
      !isLoading &&
      !isFetching &&
      unpaidLines.length > 0 &&
      !hasAutoFilled.current
    ) {
      hasAutoFilled.current = true;
      setAmounts(autoFillAmounts(unpaidLines, partialAmount));
    }
  }, [isLoading, isFetching, unpaidLines, partialAmount]);

  const total = useMemo(
    () => Object.values(amounts).reduce((sum, v) => sum + (v || 0), 0),
    [amounts]
  );

  const remaining = Math.round((partialAmount - total) * 100) / 100;
  const isValid = Math.abs(remaining) < 0.001 && total > 0;

  const partialAmountText = useMemo(
    () => formatCurrency(partialAmount, currencyCode),
    [currencyCode, partialAmount]
  );

  const allocateRetailPaymentFormattedText = useFormattedContent(
    FormatContentId.AllocateRetailPayment,
    [partialAmountText]
  );

  const handleAmountChange = useCallback(
    (lineId: number, rawVal: number) => {
      const val = isNaN(rawVal) ? 0 : rawVal;
      setAmounts((prev) => {
        const next = { ...prev, [lineId]: val };
        const lastLine = sortedLines[sortedLines.length - 1];
        if (lastLine && lineId !== lastLine.id) {
          const otherTotal = sortedLines
            .filter((l) => l.id !== lastLine.id)
            .reduce((sum, l) => sum + (next[l.id] ?? 0), 0);
          const rem = partialAmount - otherTotal;
          next[lastLine.id] = Math.max(
            0,
            Math.min(Math.round(rem * 100) / 100, lastLine.amt.amt)
          );
        }
        return next;
      });
    },
    [partialAmount, sortedLines]
  );

  const handleConfirm = useCallback(() => {
    const coverages: SaleLineItemCoverage[] = sortedLines
      .filter((sli) => (amounts[sli.id] ?? 0) > 0)
      .map((sli) => ({
        marketplacePaymentLineId: sli.id,
        amountToPay: amounts[sli.id] ?? 0,
      }));
    onConfirm(coverages);
  }, [amounts, onConfirm, sortedLines]);

  return (
    <>
      <ModalHeader onClose={onCancel}>
        <div className={styles.headerBar}>
          <h5 className={clsx(typography.title5, styles.headerText)}>
            <Content id={ContentId.AssignPayment} />
          </h5>
        </div>
      </ModalHeader>
      <ModalBody>
        <ModalBodyDataContainer style={{ height: '100%' }}>
          <Stack direction="column" gap="m">
            <HighlightText
              text={allocateRetailPaymentFormattedText as string}
              highlight={partialAmountText}
              className={styles.description}
            />

            {(isLoading || isFetching) && <PosSpinner size={vars.iconSize.s} />}

            {sortedLines.map((sli) => (
              <Stack
                key={sli.id}
                justifyContent="spaceBetween"
                alignItems="center"
                className={styles.lineItemRow}
              >
                <Stack direction="column" className={styles.lineItemDetails}>
                  <span className={styles.lineItemLabel}>
                    {LINE_TYPE_LABELS[sli.type] ?? sli.type}
                  </span>
                  <span className={styles.lineItemOriginal}>
                    {<Content id={ContentId.Original} />}:{' '}
                    {formatCurrency(sli.amt.amt, sli.amt.currency)}
                  </span>
                </Stack>
                <Stack alignItems="center" gap="s">
                  <OkButton
                    textContentId={ContentId.FullPrice}
                    variant="outline"
                    onClick={() => handleAmountChange(sli.id, sli.amt.amt)}
                    className={styles.fullButton}
                  />
                  <span className={styles.currencyLabel}>{currencyCode}</span>
                  <PosTextField
                    type="number"
                    min={0}
                    max={sli.amt.amt}
                    step="0.01"
                    value={amounts[sli.id] ?? ''}
                    placeholder="0.00"
                    rootProps={{ className: styles.amountInputRoot }}
                    onChange={(e) => {
                      handleAmountChange(sli.id, parseFloat(e.target.value));
                    }}
                  />
                </Stack>
              </Stack>
            ))}

            <Stack
              justifyContent="spaceBetween"
              alignItems="center"
              className={styles.totalRow}
            >
              <span>
                <Content id={ContentId.AssignPayment} />
              </span>
              <span
                className={
                  isValid ? styles.totalAmountValid : styles.totalAmountInvalid
                }
              >
                {formatCurrency(total, currencyCode)} / {partialAmountText}
              </span>
            </Stack>

            {!isValid && total > 0 && (
              <span
                className={
                  remaining > 0 ? styles.remainingText : styles.overText
                }
              >
                {/* TODO: replace with content id */}
                {remaining > 0
                  ? `Remaining: ${formatCurrency(remaining, currencyCode)}`
                  : `Over by ${formatCurrency(Math.abs(remaining), currencyCode)} — reduce one of the line items.`}
              </span>
            )}
          </Stack>
        </ModalBodyDataContainer>
      </ModalBody>
      <ModalFooter>
        <Stack justifyContent="end" gap="m">
          <OkButton
            textContentId={ContentId.Cancel}
            variant="outline"
            onClick={onCancel}
          />
          <OkButton
            textContentId={ContentId.Confirm}
            variant="regular"
            onClick={handleConfirm}
            disabled={!isValid || isLoading || isFetching}
          />
        </Stack>
      </ModalFooter>
    </>
  );
};
